/**
 * Abrechnung (Etappe 6) als reine Funktionen: Periodenbestimmung, Umsatzsteuer-Aufteilung, Nummern,
 * Ergebnis einer Abrechnung aus Mieterkonto-Buchungen (Plan Festlegung 2/4/14, Frage 9).
 */
import { tagPlus, monatPlus } from "./perioden";

export type KontoArt = "verkauf" | "provision" | "rabatt" | "rueckgabe" | "miete" | "kaution" | "kaution_rueck" | "auszahlung" | "uebertrag" | "schwund" | "korrektur";

export interface KontoBuchung {
  art: KontoArt;
  betragCent: number;
}

export interface AbrechnungErgebnis {
  umsatzCent: number;
  rabatteCent: number;
  rueckgabenCent: number;
  provisionCent: number;
  mieteCent: number;
  uebertragCent: number;
  sonstigesCent: number;
  /** Summe aller Buchungen = Auszahlungsbetrag (negativ = Forderung). */
  summeCent: number;
  status: "erstellt" | "uebertragen" | "forderung";
}

/** Summiert die offenen Buchungen eines Mieters und bestimmt den Status nach Mindestbetrag. */
export function abrechnungBerechnen(buchungen: KontoBuchung[], mindestbetragCent: number): AbrechnungErgebnis {
  const je = (arten: KontoArt[]) => buchungen.filter((b) => arten.includes(b.art)).reduce((a, b) => a + b.betragCent, 0);
  const umsatzCent = je(["verkauf"]);
  const rabatteCent = je(["rabatt"]);
  const rueckgabenCent = je(["rueckgabe"]);
  const provisionCent = je(["provision"]);
  const mieteCent = je(["miete"]);
  const uebertragCent = je(["uebertrag"]);
  const sonstigesCent = je(["kaution", "kaution_rueck", "auszahlung", "schwund", "korrektur"]);
  const summeCent = buchungen.reduce((a, b) => a + b.betragCent, 0);
  const status: AbrechnungErgebnis["status"] = summeCent < 0 ? "forderung" : summeCent < mindestbetragCent ? "uebertragen" : "erstellt";
  return { umsatzCent, rabatteCent, rueckgabenCent, provisionCent, mieteCent, uebertragCent, sonstigesCent, summeCent, status };
}

/** Periode, in die ein Kalendertag fällt: Monat (1.–Monatsende) oder Woche (Mo–So). */
export function periodeFuer(tag: string, art: "woche" | "monat"): { von: string; bis: string; name: string } {
  if (art === "monat") {
    const von = `${tag.slice(0, 7)}-01`;
    const bis = tagPlus(monatPlus(von, 1), -1);
    return { von, bis, name: monatsName(von) };
  }
  const d = new Date(`${tag}T00:00:00Z`);
  const wt = (d.getUTCDay() + 6) % 7;
  const von = tagPlus(tag, -wt);
  const bis = tagPlus(von, 6);
  return { von, bis, name: `Woche ${formatKurz(von)}–${formatKurz(bis)}` };
}

export function vorherigePeriode(p: { von: string }, art: "woche" | "monat") {
  return periodeFuer(tagPlus(p.von, -1), art);
}

export function monatsName(tag: string): string {
  return new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${tag}T00:00:00Z`));
}

function formatKurz(tag: string): string {
  const [, m, t] = tag.split("-");
  return `${t}.${m}.`;
}

/** Brutto in Netto/USt aufteilen (kaufmännisch gerundet). Kleinunternehmer: Satz 0. */
export function ustAufteilen(bruttoCent: number, satzProzent: number): { nettoCent: number; ustCent: number } {
  if (satzProzent <= 0) return { nettoCent: bruttoCent, ustCent: 0 };
  const netto = Math.round(bruttoCent / (1 + satzProzent / 100));
  return { nettoCent: netto, ustCent: bruttoCent - netto };
}

/** `AB-2026-10-1234` (Plan Festlegung 10) */
export function abrechnungsnummer(periodeVon: string, mieternummer: string, art: "periode" | "schluss"): string {
  return `AB-${periodeVon.slice(0, 7)}-${mieternummer}${art === "schluss" ? "-S" : ""}`;
}

/** `RL-2026-0142` mit einstellbarem Präfix */
export function mietrechnungsnummer(praefix: string, jahr: number, lfd: number): string {
  return `${praefix}${jahr}-${String(lfd).padStart(4, "0")}`;
}
